/**
 * Generic sort utility for table data. Sorts a copy of the items array
 * based on a field accessor.
 *
 * @param items - Array to sort (not mutated)
 * @param field - The sorting field key, or undefined to skip sorting
 * @param descending - Sort direction
 * @param accessor - Function to extract the comparable value from an item given a field key
 * @returns Sorted copy of items, or the original array if field is undefined
 */
export function sortItems<T>(
  items: T[],
  field: string | undefined,
  descending: boolean,
  accessor: (item: T, field: string) => string | number | undefined,
): T[] {
  if (!field) return items;

  return [...items].sort((a, b) => {
    const aVal = accessor(a, field);
    const bVal = accessor(b, field);
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const cmp =
      typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));

    return descending ? -cmp : cmp;
  });
}
